import { FC, isValidElement, ReactNode } from 'react';
import * as RxAlertDialog from '@radix-ui/react-alert-dialog';
import { withStyles, Card } from 'vopay';
import styles from './AlertDialog.module.scss';
import { Title, Button } from 'UI';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

type AlertDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onAbort?: () => void;
  onConfirm?: () => void;
  title?: ReactNode;
  content?: ReactNode;
  abortLabel?: ReactNode;
  confirmLabel?: ReactNode;
  top?: ReactNode;
  bottom?: ReactNode;
};

const AlertDialog: FC<AlertDialogProps> = ({
  isOpen,
  title,
  content,
  onClose,
  onAbort = () => {},
  onConfirm = () => {},
  abortLabel,
  confirmLabel,
  top,
  bottom,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'UI.AlertDialog' });
  return (
    <RxAlertDialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <RxAlertDialog.Overlay className={cn('alertDialogOverlay')} />

      <RxAlertDialog.Content data-testid="alert-dialog" className={cn('alertDialogContainer')}>
        <Card hoverEffect={false}>
          <div className={cn('main')}>
            <div className={cn('top')}>
              {top}
              <div className={cn('info')}>
                {title && (
                  <RxAlertDialog.Title asChild>
                    <Title level="3" className={cn('title')}>
                      {title}
                    </Title>
                  </RxAlertDialog.Title>
                )}

                {content && (
                  <RxAlertDialog.Description className={cn('description')} asChild>
                    {isValidElement(content) ? content : <div>{content}</div>}
                  </RxAlertDialog.Description>
                )}
              </div>
            </div>

            {abortLabel || confirmLabel ? (
              <div className={cn('controls')}>
                {abortLabel && (
                  <RxAlertDialog.Cancel asChild onClick={onAbort}>
                    <Button colorScheme="secondary">{abortLabel}</Button>
                  </RxAlertDialog.Cancel>
                )}
                {confirmLabel && (
                  <RxAlertDialog.Action asChild onClick={onConfirm}>
                    <Button colorScheme="primary">{confirmLabel}</Button>
                  </RxAlertDialog.Action>
                )}
              </div>
            ) : (
              <RxAlertDialog.Cancel asChild>
                <Button colorScheme="secondary">{t('closeButton')}</Button>
              </RxAlertDialog.Cancel>
            )}

            {bottom && <div className={cn('bottom')}>{bottom}</div>}
          </div>
        </Card>
      </RxAlertDialog.Content>
    </RxAlertDialog.Root>
  );
};

export default AlertDialog;
